<template>
    <div class="notification-modal">
        <header>
            <div class="left">
                <div class="title">{{ $t('notification.notifications') }}</div>
                <span class="badge" v-if="unreadNotificationCount > 0">{{ unreadNotificationCount }}</span>
            </div>
            <div class="right">
                <SupsisButton small ghost @click="toggleInclude">
                    {{ filter.includeAll ? $t('notification.showOnlyUnread') : $t('notification.showAll') }}
                </SupsisButton>
                <SupsisButton small ghost @click="markAsSeenAll">
                    <i class="material-icons">done</i>
                    {{ $t('mail.markAsAllRead') }}
                </SupsisButton>
            </div>
        </header>
        <section class="content">
            <NotificationListView
                :notifications="data.items"
                :toggleReadState="toggleReadState"
                :groupByDate="false"
                :minimized="true"
                :onCardClick="onCardClick"
                @load-more="loadMore"
            />
        </section>
        <footer>
            <SupsisButton small ghost @click="viewAll">
                <i class="material-icons">query_stats</i>
                {{ $t('notification.showAll') }}
            </SupsisButton>
            <SupsisButton small ghost @click="gotoSettings">
                <i class="material-icons">settings</i>
                {{ $t('notification.adjustSettings') }}
            </SupsisButton>
        </footer>
    </div>
</template>
<script>
import Vue from "vue";
import SupsisButton from "../Settings/components/SupsisButton.vue";
import NotificationListView from "./NotificationListView.vue";
import { ACTIVITY_API } from "../../../api/activity";
import _ from "lodash";
import { mapState, mapActions } from "vuex";
import SYSTEM_PUBSUB from "../../../utils/systemPubSub";
import { filter } from "rxjs/operators";

export default Vue.extend({
    name: "NotificationPanel",
    components: { NotificationListView, SupsisButton },
    data() {
        return {
            filter: {
                includeAll: false,
                cursor: {
                    page: 1,
                    perPage: 20,
                },
            },
            loading: true,
            notifications: [],

            data: {
                total: 0,
                items: [],
            },
            subscribes: [],
            cursor: {
                page: 1,
                perPage: 20,
            },
        };
    },
    computed: {
        ...mapState({
            popovers: (state) => state.app.headerPopoverStatus,
            unreadNotificationCount: (state) => state.app.unreadNotificationCount,
        }),
    },
    methods: {
        ...mapActions({
            setNotificationCount: "app/setNotificationCount",
        }),
        toggleReadState() {},
        onCardClick() {},
        gotoSettings() {
            this.popovers.notifications = false;
            this.$router.push("/me/notification-settings");
        },
        viewAll() {
            this.popovers.notifications = false;
            this.$router.push("/notifications");
        },
        fetchNotifications(pagination) {
            const fn = this.filter.includeAll
                ? ACTIVITY_API.NOTIFICATION.getAllNotifications
                : ACTIVITY_API.NOTIFICATION.getUnreadNotifications;
            this.loading = true;
            fn(this.cursor)
                .then((res) => {
                    const { count, items } = res.data;
                    this.data.items = _.uniqBy(pagination ? [...this.data.items, ...items] : items, (q) => q._id);
                    this.data.total = count;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        loadMore() {
            if (Math.ceil(this.data.total / this.cursor.perPage) > this.cursor.page) {
                this.cursor.page++;
            }
        },
        toggleInclude() {
            this.filter.includeAll = !this.filter.includeAll;
        },
        markAsSeenAll() {
            ACTIVITY_API.NOTIFICATION.markAsSeenAll().then(() => {
                this.data.items = this.data.items.map((item) => {
                    item.seen = true;
                    return item;
                });
                this.setNotificationCount(0);
            });
        },
    },
    mounted() {
        this.fetchNotifications();
        const notificationUpdated = SYSTEM_PUBSUB.stream
            .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.NOTIFICATION_UPDATED))
            .subscribe((x) => {
                const { id, seen } = x.payload;
                this.data.items = this.data.items.map((item) => {
                    if (item._id === id) item.seen = seen;
                    return item;
                });
            });

        this.subscribes.push(() => {
            notificationUpdated.unsubscribe();
        });
    },
    beforeDestroy() {
        this.subscribes.forEach((fn) => fn());
    },
    watch: {
        filter: {
            handler() {
                this.fetchNotifications();
            },
            deep: true,
        },
        cursor: {
            handler() {
                this.fetchNotifications(true);
            },
            deep: true,
        },
    },
});
</script>

<style lang="scss">
.notification-modal {
    display: flex;
    flex-direction: column;
    border: none;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 400;
    color: #1A1D1F;
    line-height: 1.5;
    position: relative;
    width: 750px;
    background: #FFFFFF;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    font-family: Inter, sans-serif;

    .supsis-button {
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start;
        gap: 6px;
        font-family: Inter, sans-serif;
    }

    header {
        background: #FFFFFF;
        display: flex;
        flex-flow: row;
        align-items: center;
        justify-content: space-between;
        position: relative;
        padding: 16px 20px;
        border-bottom: 1px solid #E8E8EA;
        border-radius: 12px 12px 0 0;

        .left {
            display: flex;
            gap: 8px;
            align-items: center;
            cursor: default;

            .title {
                font-size: 16px;
                font-weight: 600;
                color: #1A1D1F;
                font-family: Inter, sans-serif;
            }

            .badge {
                background: #EF4444;
                color: #FFFFFF;
                border-radius: 6px;
                margin-left: 8px;
                min-width: fit-content;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                font-size: 11px;
                font-weight: 600;
                line-height: 1;
                padding: 4px 8px;
                letter-spacing: -0.24px;
                min-height: 20px;
                text-transform: uppercase;
                width: fit-content;
                font-family: Inter, sans-serif;
            }
        }

        .right {
            display: flex;
            gap: 8px;

            .supsis-button {
                border: none;
                font-size: 13px;
                font-weight: 500;
                color: #1A1D1F;
                background-color: transparent;
                letter-spacing: -0.24px;
                line-height: 1.5;
                padding: 8px 12px;
                border-radius: 6px;
                transition: all 0.2s ease;
                font-family: Inter, sans-serif;

                &:hover {
                    background-color: #F5F3FF !important;
                    border-color: transparent !important;
                    color: #7C5CDB !important;
                }
            }
        }
    }

    footer {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        border-top: 1px solid #E8E8EA;
        border-radius: 0 0 12px 12px;
        background: #FFFFFF;

        .supsis-button:last-child {
            border-bottom: none;
        }

        .supsis-button {
            width: 100%;
            border: none !important;
            font-size: 13px;
            font-weight: 500;
            color: #1A1D1F;
            background-color: transparent;
            letter-spacing: -0.24px;
            line-height: 1.5;
            border-bottom: 1px solid #E8E8EA !important;
            border-radius: 0 !important;
            max-width: none !important;
            padding: 12px 20px;
            transition: all 0.2s ease;
            font-family: Inter, sans-serif;

            &:hover {
                background-color: #F5F3FF !important;
                border-color: transparent !important;
                color: #7C5CDB !important;
            }

            &:last-child {
                border-bottom: none !important;
                border-radius: 0 0 12px 12px !important;
            }
        }
    }

    .content {
        background: #FFFFFF;

        .loading {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 40px 20px;
        }

        .empty {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 40px 20px;
            gap: 12px;

            .material-icons {
                width: 48px;
                height: 48px;
                color: #9CA3AF;
                font-size: 48px;
            }
        }
    }
}
</style>
